import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { Delete, Edit, MoreVert } from '@tempo-io/sui-icons';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
} from 'date-fns';
import pluralize from 'pluralize';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LifecycleAction, type LifecycleActionType } from '../../../../../../../apps/frontend/src/components/idea-management/components/LifecycleActionDialog';
import { SettingsMenu } from '../../../../../../../apps/frontend/src/components/idea-management/components/SettingsMenu';
import { UserAvatar } from '../UserAvatar';
import * as Styled from './Styled';

export type GenericComment = {
  id: number;
  comment: string;
  createdAt: Date;
  internal: boolean;
  displayName?: string;
  email?: string;
  contactId?: number;
  deletedAt?: Date;
  archivedAt?: Date;
};

type CommentsProps = {
  inputIdSuffix?: string;
  disablePost?: boolean;
  postComment: (comment: string, internal: boolean) => void;
  updateComment?: (comment: GenericComment) => void;
  handleLifecycleClick?: (commentId: number, action: LifecycleActionType) => void;
  comments: GenericComment[];
  disabledInternalComments?: boolean;
  canModerateAllPosts?: boolean;
  contactId?: number;
};

const CommentInput = ({
  onSubmit,
  inputIdSuffix,
  disabledInternalComments,
}: {
  inputIdSuffix?: string;
  onSubmit: (comment: string, internal: boolean) => void;
  disabledInternalComments?: boolean;
}) => {
  const { t } = useTranslation('translation');

  const [comment, setComment] = useState('');
  const [internal, setInternal] = useState(true);

  const postComment = () => {
    if (comment === null || internal == null) return;

    onSubmit(comment, internal);

    setComment('');
  };

  useEffect(() => {
    ``;
    setComment('');
    setInternal(true);
    if (!inputIdSuffix) return;
  }, [inputIdSuffix]);

  return (
    <Styled.CommentInputContainer>
      <Styled.StyledTextField
        className="fs-exclude"
        data-dd-action-name="FIM 3 Comment - input redacted"
        value={comment}
        id={`idea-post-a-comment${inputIdSuffix}`}
        multiline
        fullWidth
        rows={3}
        placeholder={t('ideaManagement.general.writeAComment')}
        data-testid="fim3-idea-post-a-comment-input"
        onChange={(e) => setComment(e.target.value)}
      />
      <Styled.ButtonBar>
        <Box>
          {!disabledInternalComments && (
            <Styled.InternalSelect
              data-testid="fim3-idea-comment-internal-select"
              value={internal.toString().toLowerCase()}
              onChange={(e) => setInternal(e.target.value === 'true')}
            >
              <MenuItem value="false">{t('ideaManagement.general.publicComment')}</MenuItem>
              <MenuItem value="true">{t('ideaManagement.general.internalComment')}</MenuItem>
            </Styled.InternalSelect>
          )}
        </Box>
        <Button
          onClick={postComment}
          data-testid="fim3-idea-comment-post-button"
          disabled={!comment.trim()}
          variant="contained"
        >
          {t('ideaManagement.general.postComment')}
        </Button>
      </Styled.ButtonBar>
    </Styled.CommentInputContainer>
  );
};

function getElapsedText(date: Date): string {
  const now = new Date();

  const years = differenceInYears(now, date);
  if (years > 0) return `${years} ${pluralize('year', years)} ago`;

  const months = differenceInMonths(now, date);
  if (months > 0) return `${months} ${pluralize('month', months)} ago`;

  const weeks = differenceInWeeks(now, date);
  if (weeks > 0) return `${weeks} ${pluralize('week', weeks)} ago`;

  const days = differenceInDays(now, date);
  if (days > 0) return days === 1 ? 'Yesterday' : `${days} days ago`;

  const hours = differenceInHours(now, date);
  if (hours > 0) return `${hours} ${pluralize('hour', hours)} ago`;

  const minutes = differenceInMinutes(now, date);
  if (minutes > 0) return `${minutes} ${pluralize('minute', minutes)} ago`;

  return 'Just now';
}

const CommentsList = ({
  comments,
  updateComment,
  handleLifecycleClick,
  canModerateAllPosts,
  contactId,
}: {
  comments: GenericComment[];
  updateComment?: (comment: GenericComment) => void;
  handleLifecycleClick?: (commentId: number, action: LifecycleActionType) => void;
  canModerateAllPosts?: boolean;
  contactId?: number;
}) => {
  const { t } = useTranslation('translation');

  const [editingState, setEditingState] = useState<{
    id: number;
    value: string;
  }>();

  return (
    <Styled.Container>
      {comments.map((comment, index) => {
        const isArchived = !!comment.archivedAt;
        const canModerateThisPost =
          canModerateAllPosts || (!!contactId && contactId === comment.contactId);

        return (
          <Styled.Comment
            key={comment.id}
            data-testid={`fim3-comment-${comment.displayName}-${index}`}
          >
            <Styled.CommentHeader>
              <UserAvatar name={comment.displayName} email={comment.email} />
              <Styled.Name
                className="fs-exclude"
                data-dd-action-name="FIM 3 Comment - user redacted"
                data-testid={`fim3-comment-${index}-name`}
              >
                {comment.displayName ? comment.displayName : t('ideaManagement.general.anonymous')}
              </Styled.Name>
              <HeaderAnnotation dataTestId={`fim3-comment-${index}-created-at`}>
                {getElapsedText(comment.createdAt)}
              </HeaderAnnotation>

              {canModerateAllPosts ? (
                <>
                  <HeaderAnnotation dataTestId={`fim3-comment-${index}-internal`}>
                    {isArchived
                      ? t('common.archived')
                      : comment.internal
                        ? t('ideaManagement.general.internal')
                        : t('ideaManagement.general.public')}
                  </HeaderAnnotation>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <SettingsMenu
                      icon={<MoreVert className="size-6" />}
                      data-testid={`fim3-comment-${index}-menu`}
                      sx={{ opacity: '1 !important' }}
                      size="small"
                      disabled={!!editingState}
                    >
                      {!isArchived && (
                        <MenuItem
                          onClick={() => {
                            updateComment?.({
                              ...comment,
                              internal: !comment.internal,
                            } as GenericComment);
                          }}
                        >
                          {comment.internal
                            ? t('ideaManagement.general.publishToPortal')
                            : t('ideaManagement.general.removeFromPublicPortal')}
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => {
                          setEditingState({
                            id: comment.id,
                            value: comment.comment,
                          });
                        }}
                      >
                        {t('ideaManagement.general.editComment')}
                      </MenuItem>
                      {!isArchived && !!handleLifecycleClick && (
                        <MenuItem
                          data-testid="fim3-feedback-title-area-archive-feedback"
                          onClick={() => {
                            handleLifecycleClick(comment.id, LifecycleAction.Archive);
                          }}
                        >
                          {t('ideaManagement.general.archiveEntity', {
                            entityLabel: t('ideaManagement.general.comment'),
                          })}
                        </MenuItem>
                      )}
                      {isArchived && !!handleLifecycleClick && (
                        <MenuItem
                          data-testid="fim3-feedback-title-area-restore-feedback"
                          onClick={() => {
                            handleLifecycleClick(comment.id, LifecycleAction.Restore);
                          }}
                        >
                          {t('ideaManagement.general.restoreEntity', {
                            entityLabel: t('ideaManagement.general.comment'),
                          })}
                        </MenuItem>
                      )}
                      {isArchived && !!handleLifecycleClick && (
                        <MenuItem
                          data-testid="fim3-feedback-title-area-delete-feedback"
                          onClick={() => {
                            handleLifecycleClick(comment.id, LifecycleAction.Delete);
                          }}
                        >
                          {t('ideaManagement.general.deleteEntity', {
                            entityLabel: t('ideaManagement.general.comment'),
                          })}
                        </MenuItem>
                      )}
                    </SettingsMenu>
                  </Box>
                </>
              ) : canModerateThisPost ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      setEditingState({
                        id: comment.id,
                        value: comment.comment,
                      })
                    }
                    disabled={!!editingState}
                  >
                    <Edit className="size-6" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleLifecycleClick?.(comment.id, LifecycleAction.Delete)}
                    disabled={!!editingState}
                  >
                    <Delete className="size-6" />
                  </IconButton>
                </Box>
              ) : comment.internal ? (
                <HeaderAnnotation dataTestId={`fim3-comment-${index}-internal`}>
                  {t('ideaManagement.general.internal')}
                </HeaderAnnotation>
              ) : null}
            </Styled.CommentHeader>
            {editingState && editingState.id === comment.id ? (
              <ClickAwayListener onClickAway={() => setEditingState(undefined)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    className="fs-exclude"
                    data-dd-action-name="FIM 3 Comment - input redacted"
                    defaultValue={comment.comment}
                    id={`edit-a-comment${index}`}
                    multiline
                    fullWidth
                    rows={3}
                    onChange={(e) => {
                      setEditingState({
                        id: comment.id,
                        value: e.target.value,
                      });
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                      className="lowercase"
                      size="small"
                      onClick={() => {
                        setEditingState(undefined);
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        if (editingState === undefined || updateComment === undefined) return;
                        updateComment?.({
                          ...comment,
                          comment: editingState?.value ?? '',
                        } as GenericComment);
                        setEditingState(undefined);
                      }}
                      data-testid={`fim3-comment-${index}-save-button`}
                      variant="contained"
                    >
                      {t('ideaManagement.general.update')}
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            ) : (
              <p
                className={`fs-exclude ml-3 whitespace-pre-wrap text-sm leading-normal [overflow-wrap:anywhere]${!canModerateAllPosts && canModerateThisPost ? ' cursor-pointer' : ''}`}
                data-dd-action-name="FIM 3 Comment - comment redacted"
                data-testid={`fim3-comment-${index}-text`}
                onClick={() => {
                  if (!canModerateAllPosts && canModerateThisPost) {
                    setEditingState({ id: comment.id, value: comment.comment });
                  }
                }}
              >
                {comment.comment}
              </p>
            )}
          </Styled.Comment>
        );
      })}
    </Styled.Container>
  );
};

const HeaderAnnotation = ({
  children,
  dataTestId,
  archived,
}: {
  children: string | React.ReactNode;
  dataTestId?: string;
  archived?: boolean;
}) => (
  <>
    <Styled.StyledDot sx={{ opacity: archived ? 0.5 : 1 }} />
    <Styled.SubText
      style={{ opacity: archived ? 0.5 : 1 }}
      className="fs-exclude"
      data-dd-action-name="FIM 3 Comment - subtext redacted"
      data-testid={dataTestId}
    >
      {children}
    </Styled.SubText>
  </>
);

export const Comments = ({
  disablePost,
  postComment,
  comments,
  inputIdSuffix,
  updateComment,
  disabledInternalComments,
  handleLifecycleClick,
  canModerateAllPosts,
  contactId,
}: CommentsProps) => {
  return (
    <Styled.Container data-testid="tempo-comments">
      {!disablePost && (
        <CommentInput
          onSubmit={postComment}
          inputIdSuffix={inputIdSuffix}
          disabledInternalComments={disabledInternalComments}
        />
      )}
      <CommentsList
        comments={comments ?? []}
        updateComment={updateComment}
        handleLifecycleClick={handleLifecycleClick}
        canModerateAllPosts={canModerateAllPosts}
        contactId={contactId}
      />
    </Styled.Container>
  );
};
